export interface VideoStats {
  resolution: { width: number; height: number };
  bitrate: number;
  fps: number;
}

// Store previous bitrate calculation data
let previousStats: { bytesSent: number; timestamp: number } | null = null;

/**
 * Extract real video stats from WebRTC RTCStatsReport
 */
export async function extractVideoStats(
  peerConnection: RTCPeerConnection,
  isViewer: boolean = false
): Promise<VideoStats> {
  try {
    const stats = await peerConnection.getStats();

    // Default values
    let resolution = { width: 0, height: 0 };
    let bitrate = 0;
    let fps = 0;

    // Extract stats from WebRTC reports
    stats.forEach((report) => {
      if (isViewer) {
        // For viewer mode: Get video track stats from inbound RTP (receiver side)
        if (report.type === "inbound-rtp" && report.mediaType === "video") {
          resolution = {
            width: report.frameWidth || 0,
            height: report.frameHeight || 0,
          };
          fps = report.framesPerSecond || 0;

          // Calculate bitrate for received video
          if (report.bytesReceived && report.timestamp && previousStats) {
            const bytesDiff = report.bytesReceived - previousStats.bytesSent;
            const timeDiff = (report.timestamp - previousStats.timestamp) / 1000;

            if (timeDiff > 0) {
              bitrate = Math.round((bytesDiff * 8) / timeDiff);
            }
          }

          // Store current stats for next calculation
          if (report.bytesReceived && report.timestamp) {
            previousStats = { bytesSent: report.bytesReceived, timestamp: report.timestamp };
          }
        }
      } else {
        // For camera mode: Get video track stats from outbound RTP (sender side)
        if (report.type === "outbound-rtp" && report.mediaType === "video") {
          resolution = {
            width: report.frameWidth || 0,
            height: report.frameHeight || 0,
          };
          fps = report.framesPerSecond || 0;

          // Calculate bitrate properly using previous measurement
          if (report.bytesSent && report.timestamp && previousStats) {
            const bytesDiff = report.bytesSent - previousStats.bytesSent;
            const timeDiff = (report.timestamp - previousStats.timestamp) / 1000; // Convert to seconds

            if (timeDiff > 0) {
              bitrate = Math.round((bytesDiff * 8) / timeDiff); // bits per second
            }
          }

          // Store current stats for next calculation
          if (report.bytesSent && report.timestamp) {
            previousStats = { bytesSent: report.bytesSent, timestamp: report.timestamp };
          }
        }
      }

      // Alternative: media-source stats (some browsers)
      if (report.type === "media-source" && report.kind === "video") {
        if (report.width && report.height) {
          resolution = { width: report.width, height: report.height };
        }
        if (report.framesPerSecond) {
          fps = report.framesPerSecond;
        }
      }

      // Alternative: track stats (fallback)
      if (report.type === "track" && report.kind === "video") {
        if (report.frameWidth && report.frameHeight) {
          resolution = {
            width: report.frameWidth,
            height: report.frameHeight,
          };
        }
        if (report.framesPerSecond) {
          fps = report.framesPerSecond;
        }
      }
    });

    // Fallback: try to get resolution from actual stream if WebRTC stats are empty
    if (resolution.width === 0 && resolution.height === 0) {
      if (isViewer) {
        // For viewer mode: get resolution from receivers
        const receivers = peerConnection.getReceivers();
        const videoReceiver = receivers.find((receiver) => receiver.track?.kind === "video");

        if (videoReceiver?.track) {
          const settings = videoReceiver.track.getSettings();

          resolution = {
            width: settings.width || 0,
            height: settings.height || 0,
          };

          if (settings.frameRate) {
            fps = settings.frameRate;
          }
        }
      } else {
        // For camera mode: get resolution from senders
        const senders = peerConnection.getSenders();
        const videoSender = senders.find((sender) => sender.track?.kind === "video");

        if (videoSender?.track) {
          const settings = videoSender.track.getSettings();

          resolution = {
            width: settings.width || 0,
            height: settings.height || 0,
          };

          if (settings.frameRate) {
            fps = settings.frameRate;
          }
        }
      }
    }

    // Fallback bitrate estimation if WebRTC doesn't provide it
    if (bitrate === 0 && resolution.width > 0 && resolution.height > 0 && fps > 0) {
      // Estimate bitrate based on resolution and quality level
      const pixels = resolution.width * resolution.height;
      const pixelsPerSecond = pixels * fps;

      // Conservative bitrate estimation (bits per pixel per second)
      let bitsPerPixel = 0.1; // Default conservative estimate for high-res video

      bitrate = Math.round(pixelsPerSecond * bitsPerPixel);
    }

    return {
      resolution,
      bitrate,
      fps,
    };
  } catch (error) {
    // Return default stats if extraction fails
    return {
      resolution: { width: 0, height: 0 },
      bitrate: 0,
      fps: 0,
    };
  }
}

/**
 * Format bitrate for display (e.g., "8.5M", "2.1K")
 */
export function formatBitrate(bitrate: number): string {
  if (bitrate >= 1000000) {
    return `${(bitrate / 1000000).toFixed(1)}M`;
  } else if (bitrate >= 1000) {
    return `${(bitrate / 1000).toFixed(0)}K`;
  }
  return `${bitrate}`;
}
